import { buildRequestInit } from '@datocms/cda-client'

interface DatoCMSResponse<T> {
  data: T
  errors?: Array<{ message: string }>
}

interface GraphQLRequest<V = Record<string, any>> {
  query: string
  variables?: V
  includeDrafts?: boolean
  excludeInvalid?: boolean
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()

  const apiToken =
    config.public.environment === 'production'
      ? config.public.datocmsPublishedContentCdaToken
      : config.datocmsDraftContentCdaToken

  const baseURL =
    config.public.environment === 'production'
      ? 'https://graphql.datocms.com'
      : 'https://graphql.datocms.com/preview'

  const fetchInstance = $fetch.create<DatoCMSResponse<unknown>>({
    baseURL,
    async onResponse({ response }) {
      const data = response._data as DatoCMSResponse<unknown>
      if (data.errors) {
        const errorMessages = data.errors.map((error) => error.message).join('\n')
        throw new Error(`DatoCMS API Error:\n${errorMessages}`)
      }
      response._data = data.data
    }
  })

  function datocmsClient<T>(request: GraphQLRequest): Promise<T> {
    const {
      query,
      variables,
      includeDrafts = config.public.environment !== 'production',
      excludeInvalid = true
    } = request

    const requestInit = buildRequestInit(query, {
      token: apiToken,
      variables,
      includeDrafts,
      excludeInvalid,
      environment: config.public.datocmsEnvironmentId
    })

    return fetchInstance<DatoCMSResponse<T>>('', {
      ...requestInit,
      method: requestInit.method as 'POST'
    }).then((response) => response as T)
  }

  return {
    provide: {
      datocmsClient
    }
  }
})
